import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card } from "react-bootstrap";

import KpiSection from "./components/KpiSection";
import ChartSection from "./components/ChartSection";
import Top5Table from "./components/Top5Table";
import PeriodFilter from "./components/filters/PeriodFilter";

import { formatPeriodLabel } from "./utils/formatPeriodLabel";
import { validatePeriod } from "./utils/validatePeriod";

import DashboardService from "../../services/pbumku/DashboardService";

import { KPI_ICONS } from "./constants/kpiIcons";
import { KPI_COLORS } from "./constants/kpiColors";
import { IZIN_MAP } from "./constants/izinMap";

/* =========================
 * DATE UTIL
 * ========================= */
const currentYear = new Date().getFullYear();
const getStartOfYear = () => `${currentYear}-01-01`;
const getEndOfYear = () => `${currentYear}-12-31`;

export default function DashPrimer() {
  const DashService = useMemo(() => new DashboardService(), []);

  const [loading, setLoading] = useState(false);
  const [rekapPrimer, setRekapPrimer] = useState(null);
  const [chartData, setChartData] = useState({});

  // TEMP (form)
  const [tempStartDate, setTempStartDate] = useState(getStartOfYear());
  const [tempEndDate, setTempEndDate] = useState(getEndOfYear());

  // APPLIED (dashboard)
  const [appliedStartDate, setAppliedStartDate] =
    useState(getStartOfYear());
  const [appliedEndDate, setAppliedEndDate] =
    useState(getEndOfYear());

  const handleClickButton = () => {
    const validation = validatePeriod(tempStartDate, tempEndDate, {
      maxYearRange: 1,
      allowCrossYear: true,
    });

    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    setAppliedStartDate(tempStartDate);
    setAppliedEndDate(tempEndDate);
  };

  useEffect(() => {
    fetchRekap();
    fetchAllCharts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedStartDate, appliedEndDate]);

  const fetchRekap = async () => {
    try {
      setLoading(true);

      const start = appliedStartDate?.slice(0, 10);
      const end = appliedEndDate?.slice(0, 10);

      const res = await DashService.getRekapPermohonan(start,end,7);
      setRekapPrimer(res.data);
    } catch (err) {
      console.error("Fetch rekap error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartPerIzin = async (key) => {
    const start = appliedStartDate.slice(0, 10);
    const end = appliedEndDate.slice(0, 10);

    const res = await DashService.getPropinsiPerIzin(
      start,
      end,
      IZIN_MAP[key],
      5
    );

    return res?.data?.data || [];
  };

  const fetchAllCharts = async () => {
    try {
      const results = await Promise.all(
        Object.keys(IZIN_MAP).map(async (key) => ({
          key,
          data: await fetchChartPerIzin(key),
        }))
      );

      setChartData(
        results.reduce((acc, cur) => {
          acc[cur.key] = cur.data;
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("Fetch chart error:", err);
    }
  };

  return (
    <>
      {/* ===== HEADER ===== */}
      <Card className="mb-6 bg-transparent border-0 shadow-none">
        <Card.Body className="px-0">
          <Row className="align-items-end">
            {/* ===== LEFT: TITLE ===== */}
            <Col md={6}>
              <h3 className="text-white fw-bold mb-2">
                SERTIFIKASI MUTU PRIMER
              </h3>
              <h5 className="text-white-50">
                {formatPeriodLabel(
                  appliedStartDate,
                  appliedEndDate
                )}
              </h5>
            </Col>

            {/* ===== RIGHT: FILTER ===== */}
            <Col
              md={6}
              className="d-flex justify-content-md-end mt-3 mt-md-0"
            >
              <PeriodFilter
                startDate={tempStartDate}
                endDate={tempEndDate}
                onChangeStart={setTempStartDate}
                onChangeEnd={setTempEndDate}
                onSubmit={handleClickButton}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-4">
        {/* ===== KPI & TABLE ===== */}
        <Col xl={5}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h6 className="fw-semibold mb-3">Analytics</h6>

              <KpiSection
                rekap={rekapPrimer?.rekap || {}}
                loading={loading}
                icons={KPI_ICONS}
                colors={KPI_COLORS}
              />

              <div className="mt-0">
                <Top5Table data={rekapPrimer?.data || []} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* ===== CHART ===== */}
        <Col xl={7}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <ChartSection
                izinKeys={Object.keys(IZIN_MAP)}
                chartData={chartData}
                colors={KPI_COLORS}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
